import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IDisposable } from "Global/Base/IDisposable";
import { IStoreOriginalPrototype } from "Global/Base/Mixins/StoreOriginalPrototypeMixin";
import { InvalidArgumentException } from "../../../Global/Base/Exceptions";
import { LogicalOperator as LogicalOperators } from "../../../Global/Base/LogicalOperations";
import { EvaluationFunctionType, IOverridableTypeInfo, OverridableType } from "../../../Global/Base/Rights/IRightsEvaluationOverrides";
import { RightType } from "../../../Global/Base/Rights/RightTypes";
import { ILogicalComponent } from "../LogicalTree/ILogicalComponent";
import { IGenericControl } from "../UI/Controls/ControlInterfaces";
import { IFrameWithRights } from "../UI/XGControls/IFrame";
import { getControlType } from "../Utilities/SharedUIMethods";
import { AdditionalRightResultInfoType, IBasicRightResults } from "./BasicRightsResults";
import { RightsEvaluationOverridesType, RightTypeEvaluationFunctionType } from "./Definitions";

const logger = LazyLoadingSectionLogger.create(LoggingSection.rights);

export abstract class RightsEvaluationOverridesBase implements RightsEvaluationOverridesType, IDisposable {
    private _registeredTypes: Map<string, IOverridableTypeInfo> = new Map<string, IOverridableTypeInfo>();
    private _typesOverrides: Map<string, RightTypeEvaluationFunctionType[]> = new Map<string, RightTypeEvaluationFunctionType[]>();
    private _ignoredTypes: Set<OverridableType<any>> = new Set<OverridableType<any>>();

    protected readonly __frame: IFrameWithRights;
    private __operatorsForRightTypes: Map<RightType, LogicalOperators> = new Map<RightType, LogicalOperators>();

    constructor(frame: IFrameWithRights) {
        this.__frame = frame;
        this._initializeOverrides();
        this._initializeOperators();
    }

    protected get _frame(): IFrameWithRights {
        return this.__frame;
    }

    protected get _operatorsForRightTypes(): Map<RightType, LogicalOperators> {
        return this.__operatorsForRightTypes;
    }

    // copy-paste z DIContainer
    private _getIdentifierName(typeIdentifier: OverridableType<any>): string {
        switch (typeof typeIdentifier) { // typeof XGAction === 'function'; typeof CryptoAction === 'function'
            case "function":
                return (typeIdentifier as any).name; // pokud bych to napsal do jako if, tak by tam nemuselo být přetypování na any, ale tohle TypeScript neumí
            case "string":
                return typeIdentifier;
            case "symbol":
                return /Symbol\((\w*)\)/.exec((typeIdentifier as any).toString())![1];
        }
        throw new InvalidArgumentException("typeIdentifier");
    }

    /**
     * Zaregistruje si všechny overrides definované na této třídě
     */
    protected _initializeOverrides(): void {
        // Prázdná metoda
    }

    protected _initializeOperators(): void {
        // Prázdná metoda
    }

    private _registerType<TComponentType>(typeIdentifier: OverridableType<TComponentType>): void {
        const identifierName = this._getIdentifierName(typeIdentifier);
        if (!this._registeredTypes.has(identifierName)) {
            this._registeredTypes.set(identifierName, { overrideType: typeIdentifier });
            this._typesOverrides.set(identifierName, []);
        }
    }

    /**
     * Zaregistruje nový typ a nastaví mu příznak, že jde o interface. Pokud už byl zaregistrován dříve, pouze nastaví příznak.
     * @param interfaceTypeIdentifier Interfacový typ k zaregistrování/nastavení
     */
    protected _registerInterfaceType<TComponentType>(interfaceTypeIdentifier: OverridableType<TComponentType>): boolean {
        this._registerType(interfaceTypeIdentifier);
        const identifierName = this._getIdentifierName(interfaceTypeIdentifier);
        const interfaceType = this._registeredTypes.get(identifierName);
        if (interfaceType) {
            interfaceType.isInterface = true;
            return true;
        }

        return false;
    }

    /**
     * Zaregistruje override funkce pro daný typ komponenty
     * @param typeIdentifier
     * @param rightType
     * @param evaluationFunction
     * @param asGeneric
     */
    protected _registerOverrideFunctionForRightType<TComponentType>(typeIdentifier: OverridableType<TComponentType>, rightType: RightType, evaluationFunction: EvaluationFunctionType<ILogicalComponent, boolean | null, IBasicRightResults>, asGeneric?: boolean): void {
        const identifierName = this._getIdentifierName(typeIdentifier);
        if (!this._registeredTypes.has(identifierName)) {
            this._registerType(typeIdentifier);
        }
        if (this._typesOverrides.has(identifierName)) {
            const evaluationFunctionsArray = this._typesOverrides.get(identifierName);
            //const thisOverrides = this;
            const newRightTypeEvaluationFunction: RightTypeEvaluationFunctionType = {
                rightType: rightType,
                evaluationFunction: (component: ILogicalComponent, basicRightResult: boolean | null, allBasicRightsResult: IBasicRightResults) => {
                    if (asGeneric || !asGeneric && this._isDirectImplementation(component.control, typeIdentifier))
                        /*Přetypováno na any, kvůli tsc nastavení strictBindCallApply. This argument nesplňuje signaturu metody. */
                        return (evaluationFunction as any).call(this, component, basicRightResult, allBasicRightsResult); /* Call, abychom bindnuli this */
                    return null;
                },
            };
            evaluationFunctionsArray.push(newRightTypeEvaluationFunction);
        } else
            logger.warn(); // TODO-rites
    }

    private _isDirectImplementation<TComponentType>(control: IGenericControl, typeIdentifier: OverridableType<TComponentType>): boolean {
        if (control instanceof IStoreOriginalPrototype) {
            return control.originalPrototype.constructor === typeIdentifier;
        } else {
            return control.constructor === typeIdentifier;
        }
    }

    public getOverridesForComponent<TComponentType>(controlWrapper: ILogicalComponent): RightTypeEvaluationFunctionType[] | null {
        const typeIdentifier: OverridableType<TComponentType> = getControlType(controlWrapper.control);
        let resultOverrides: RightTypeEvaluationFunctionType[] = [];
        const identifierName = this._getIdentifierName(typeIdentifier);
        if (this._typesOverrides.has(identifierName)) {
            resultOverrides = resultOverrides.concat(this._typesOverrides.get(identifierName));
        }
        //else
        {
            //TODO-rites: X c1: projít, zda některý ze zaregistrovaných typů není (ifacovým) předkem hledaného; c2: asi zatím neřešit; c3: vyřešeno pro třídy (ne pro ifacy)
            for (const [registeredTypeIdentifier, registeredTypeInfo] of this._registeredTypes) {
                const registeredType = registeredTypeInfo.overrideType;
                // Pokud jde o interface, tak se musí kontrolovat přes instanceof, aby se využila kontrolní interfacová třída.
                if (registeredType instanceof Function && ((!registeredTypeInfo.isInterface && Object.prototype.isPrototypeOf.call(registeredType, typeIdentifier)) || (registeredTypeInfo.isInterface && controlWrapper.control instanceof registeredType))) {
                    //const overrideFunction = this._typesOverrides.get(registeredTypeIdentifier);
                    //if (overrideFunction && overrideFunction.)
                    resultOverrides = resultOverrides.concat(this._typesOverrides.get(registeredTypeIdentifier));
                }
            }

            //logger.warn(); // TODO-rites: X ??
            //return null;
        }

        if (resultOverrides.length > 0)
            return resultOverrides;

        return null;
    }

    public isIgnored<TComponentType>(typeIdentifier: OverridableType<TComponentType>): boolean {
        return this._ignoredTypes.has(typeIdentifier);
    }

    protected _registerIgnoredType<TComponentType>(typeIdentifier: OverridableType<TComponentType>): void {
        this._ignoredTypes.add(typeIdentifier);
    }

    public getOperatorForRightType(rightType: RightType): LogicalOperators | null {
        const operatorResult = this._operatorsForRightTypes.get(rightType);

        return operatorResult;
    }

    protected _getDisabledFromParent(allBasicRightsResult?: IBasicRightResults): boolean {
        return allBasicRightsResult?.getAdditionalResultInfo(AdditionalRightResultInfoType.disabledFormParent) ?? false;
    }

    public dispose(): void {
        this._registeredTypes.clear();
        this._typesOverrides.clear();
        this._ignoredTypes.clear();
        this.__operatorsForRightTypes.clear();
    }
}

//(() => {
//    const rightsOverrides = new RightsEvaluationOverrides(null);

//    rightsOverrides.registerType(MethodAction);
//    rightsOverrides.registerOverrideFunctionForRightType(MethodAction, RightType.enabled, () => {});

//    rightsOverrides.registerType(IAction);

//    // @ts-ignore // Kvůli nepoužité proměnné
//    const xgActionOverrides = rightsOverrides.getOverridesForComponent(XGAction);
//    // @ts-ignore // Kvůli nepoužité proměnné
//    const methodActionOverrides = rightsOverrides.getOverridesForComponent(MethodAction);
//    // @ts-ignore // Kvůli nepoužité proměnné
//    const cryptoActionOverrides = rightsOverrides.getOverridesForComponent(CryptoAction);
//})();
